import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useEffect } from "react";

const testimonials = [
  {
    quote: "The Content Creation module taught me how to craft posts that actually engage. Within 2 months, I landed my first 3 clients paying 25K each monthly. The portfolio I built during training was key.",
    name: "Mary Njeri",
    role: "Content Creator & Social Media Manager",
    location: "Nairobi",
    skill: "Content Creation",
    result: "3 Clients, 75K Monthly Income"
  },
  {
    quote: "Meta Ads training changed everything for my salon. I spent 5K on my first campaign and got 23 bookings that week. Now I run ads consistently and my salon is fully booked.",
    name: "Faith Akinyi",
    role: "Salon Owner & Meta Ads Specialist",
    location: "Eastleigh",
    skill: "Meta Ads Mastery",
    result: "Salon Fully Booked + Side Consulting"
  },
  {
    quote: "My first TikTok ad cost 3K and brought in 47K in sales in one week. Now I allocate 10K monthly for TikTok ads and consistently make 6x returns.",
    name: "Grace Muthoni",
    role: "Electronics Shop Owner",
    location: "Thika",
    skill: "TikTok Advertising",
    result: "6x Return on Ad Spend"
  },
  {
    quote: "Janet taught us Canva and CapCut professionally. Now I edit promotional videos in under 30 minutes, charging 5K per video. I have 8 regular clients making 95K monthly.",
    name: "David Omondi",
    role: "Freelance Video Editor",
    location: "Ruiru",
    skill: "Video Production",
    result: "8 Regular Clients, 95K Monthly"
  },
  {
    quote: "Client Communication training taught me how to set expectations and maintain professional relationships. My client retention went from 40% to 85%. I now manage 6 brands long-term.",
    name: "Christine Wambui",
    role: "Freelance Digital Marketer",
    location: "Kiambu",
    skill: "Client Communication",
    result: "85% Client Retention Rate"
  },
  {
    quote: "Sales & Conversion module taught me WhatsApp sales techniques and how to close deals. I now convert 6 out of 10 inquiries. My monthly revenue doubled from 45K to 90K.",
    name: "Kevin Mwangi",
    role: "Barber Shop Owner",
    location: "Nairobi CBD",
    skill: "Sales & Conversion",
    result: "Revenue Doubled to 90K Monthly"
  },
  {
    quote: "After Meta Ads training, I spent a client's 8K budget and generated 67K in sales for them. Word spread, and now I manage ads for 5 businesses, earning 120K monthly.",
    name: "Brian Mutua",
    role: "Meta Ads Specialist",
    location: "Thika",
    skill: "Meta Ads Mastery",
    result: "5 Business Clients, 120K Monthly"
  },
  {
    quote: "The portfolio I created during Content Creation training got me hired. I applied to 12 companies and got hired as Social Media Coordinator earning 65K plus benefits.",
    name: "Dennis Kipchirchir",
    role: "Social Media Coordinator",
    location: "Nairobi",
    skill: "Content Creation",
    result: "Corporate Job, 65K + Benefits"
  },
  {
    quote: "Sales & Conversion training taught me the psychology behind buying decisions. My conversion rate went from 2% to 18%, and monthly revenue increased from 35K to 110K.",
    name: "Esther Wanjiru",
    role: "Online Boutique Owner",
    location: "Ngong Road",
    skill: "Sales & Conversion",
    result: "Revenue Tripled to 110K Monthly"
  },
  {
    quote: "Video Production training gave me a skill I could monetize immediately. Within my first month, I made 45K. Three months later, I'm earning 60K consistently.",
    name: "Anthony Kamau",
    role: "Freelance Video Editor",
    location: "Ruiru",
    skill: "Video Production",
    result: "60K Monthly Freelancing"
  },
  {
    quote: "TikTok Advertising module opened my eyes. My first campaign for my catering business brought 31 event bookings in 2 weeks. I've increased monthly bookings by 300%.",
    name: "Lucy Achieng",
    role: "Catering Business Owner",
    location: "Embakasi",
    skill: "TikTok Advertising",
    result: "Bookings Increased 300%"
  },
  {
    quote: "Client Communication skills saved my freelancing career. I implemented proper onboarding and regular updates. My repeat client rate went from 30% to 90%.",
    name: "Peter Otieno",
    role: "Freelance Social Media Manager",
    location: "Nairobi",
    skill: "Client Communication",
    result: "90% Client Retention, More Referrals"
  }
];

export function TestimonialSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState(0);

  const getItemsPerView = () => {
    if (typeof window === 'undefined') return 1;
    if (window.innerWidth >= 1024) return 3;
    if (window.innerWidth >= 768) return 2;
    return 1;
  };

  const [itemsPerView, setItemsPerView] = useState(getItemsPerView());

  useEffect(() => {
    const handleResize = () => {
      setItemsPerView(getItemsPerView());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      handleNext();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [currentIndex, isAutoPlaying, itemsPerView]);

  const maxIndex = Math.max(0, testimonials.length - itemsPerView);

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const handlePrev = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const visibleTestimonials = testimonials.slice(
    currentIndex,
    currentIndex + itemsPerView
  );

  return (
    <section className="relative py-12 md:py-16 lg:py-20 bg-gradient-to-b from-[hsl(0,100%,8%)] via-[hsl(0,90%,10%)] to-[hsl(0,100%,8%)] overflow-hidden">
      {/* Premium Background Pattern - Dark Maroon Theme */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-radial from-maroon/10 via-transparent to-transparent blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-radial from-turquoise/10 via-transparent to-transparent blur-3xl" />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      <div className="container relative z-10 px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-turquoise/10 to-maroon/10 rounded-full border border-turquoise/20 mb-6">
            <span className="text-sm font-semibold text-white">Real Success Stories</span>
          </div>
          
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-5xl font-bold mb-6 leading-tight text-white">
            From Struggle to{" "}
            <span className="text-turquoise">Success</span>
          </h2>
          
          <p className="text-gray-300 text-base md:text-lg leading-relaxed">
            Real people, real breakthroughs. <span className="text-white font-semibold">Lives transformed through skills and opportunity.</span>
          </p>
        </div>

        {/* Carousel Container */}
        <div 
          className="relative max-w-7xl mx-auto"
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}
        >
          {/* Navigation Buttons */}
          <button
            onClick={handlePrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 z-20 
                       w-12 h-12 rounded-full bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] border border-white/10 shadow-2xl
                       flex items-center justify-center transition-all duration-300
                       hover:border-turquoise/50 hover:shadow-turquoise/20 hover:scale-110
                       disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={currentIndex === 0}
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>

          <button
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 z-20
                       w-12 h-12 rounded-full bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] border border-white/10 shadow-2xl
                       flex items-center justify-center transition-all duration-300
                       hover:border-turquoise/50 hover:shadow-turquoise/20 hover:scale-110
                       disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={currentIndex === maxIndex}
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>

          {/* Testimonials Carousel */}
          <div className="overflow-hidden px-2">
            <div className={`grid gap-5 lg:gap-6 transition-all duration-700 ease-out ${
              itemsPerView === 3 ? 'md:grid-cols-2 lg:grid-cols-3' : 
              itemsPerView === 2 ? 'md:grid-cols-2' : 
              'grid-cols-1'
            }`}>
              {visibleTestimonials.map((testimonial, index) => (
                <div
                  key={`${currentIndex}-${index}`}
                  className="animate-slide-in-testimonial"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <Card className="border border-white/10 hover:border-turquoise/50 transition-all duration-500 
                                   bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)] h-full
                                   hover:shadow-2xl hover:shadow-turquoise/20 hover:-translate-y-2
                                   group">
                    <CardContent className="p-6 md:p-7 lg:p-8 relative overflow-hidden h-full flex flex-col">
                      {/* Subtle gradient overlay on hover */}
                      <div className="absolute inset-0 bg-gradient-to-br from-turquoise/5 via-transparent to-maroon/5 
                                    opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      <div className="relative z-10 flex flex-col h-full">
                        {/* Skill Badge */}
                        <div className="mb-4">
                          <span className="inline-block px-3 py-1.5 rounded-full bg-turquoise/10 border border-turquoise/30 text-xs font-semibold text-turquoise">
                            {testimonial.skill}
                          </span>
                        </div>

                        {/* Quote */}
                        <blockquote className="text-base md:text-lg text-gray-300 leading-relaxed mb-6 flex-grow italic">
                          "{testimonial.quote}"
                        </blockquote>

                        {/* Result */}
                        <div className="mb-6 pb-6 border-b border-white/10">
                          <p className="text-sm font-bold text-turquoise">
                            {testimonial.result}
                          </p>
                        </div>

                        {/* Author */}
                        <div className="mt-auto">
                          <p className="font-heading font-bold text-lg text-white mb-1">
                            {testimonial.name}
                          </p>
                          <p className="text-sm text-gray-400">
                            {testimonial.role}
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            {testimonial.location}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          {/* Progress Indicators */}
          <div className="flex justify-center gap-2 mt-10">
            {Array.from({ length: maxIndex + 1 }).map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                }}
                className={`h-2 rounded-full transition-all duration-500 ${
                  index === currentIndex 
                    ? 'w-12 bg-gradient-to-r from-turquoise to-maroon' 
                    : 'w-2 bg-white/20 hover:bg-turquoise/50'
                }`}
                aria-label={`Go to testimonial group ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-20 max-w-5xl mx-auto">
          <Card className="border border-white/10 bg-gradient-to-br from-[hsl(186,70%,12%)] to-[hsl(186,60%,8%)]
                         shadow-2xl overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-r from-turquoise/5 via-transparent to-maroon/5" />
            
            <CardContent className="p-8 lg:p-12 relative z-10">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                <div className="text-center space-y-3 group">
                  <div className="text-4xl md:text-5xl lg:text-5xl font-heading font-bold text-white 
                                transition-all duration-500 group-hover:scale-110 group-hover:text-turquoise">
                    500+
                  </div>
                  <p className="text-sm text-gray-300 font-semibold">Lives Transformed</p>
                </div>

                <div className="text-center space-y-3 group">
                  <div className="text-4xl md:text-5xl lg:text-5xl font-heading font-bold text-white
                                transition-all duration-500 group-hover:scale-110 group-hover:text-turquoise">
                    85%
                  </div>
                  <p className="text-sm text-gray-300 font-semibold">Now Earning Income</p>
                </div>

                <div className="text-center space-y-3 group">
                  <div className="text-4xl md:text-5xl lg:text-5xl font-heading font-bold text-white
                                transition-all duration-500 group-hover:scale-110 group-hover:text-turquoise">
                    200+
                  </div>
                  <p className="text-sm text-gray-300 font-semibold">Job Referrals Made</p>
                </div>

                <div className="text-center space-y-3 group">
                  <div className="text-4xl md:text-5xl lg:text-5xl font-heading font-bold text-white
                                transition-all duration-500 group-hover:scale-110 group-hover:text-turquoise">
                    4.9/5
                  </div>
                  <p className="text-sm text-gray-300 font-semibold">Student Satisfaction</p>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-white/10 text-center">
                <p className="text-base md:text-lg text-gray-300">
                  <span className="font-bold text-turquoise">Beyond training:</span> Janet connects you with her network of businesses seeking your skills
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center max-w-2xl mx-auto">
          <p className="text-xl md:text-2xl font-bold text-white mb-3">
            Your transformation starts here
          </p>
          <p className="text-gray-300 text-base mb-2">
            Next Intake: <span className="font-bold text-turquoise">February 17, 2026</span>
          </p>
          <p className="text-sm text-gray-400">
            Physical (Ruiru) & Virtual Options Available
          </p>
        </div>
      </div>

      <style>{`
        .bg-gradient-radial {
          background: radial-gradient(circle, var(--tw-gradient-stops));
        }

        @keyframes slide-in-testimonial {
          from {
            opacity: 0;
            transform: translateX(${direction > 0 ? '50px' : '-50px'}) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        .animate-slide-in-testimonial {
          animation: slide-in-testimonial 0.6s cubic-bezier(0.4, 0, 0.2, 1) backwards;
        }
      `}</style>
    </section>
  );
}